import { useEffect, useState } from "react";

/** `useState` whose value survives navigating away and back (and a reload), for this
 * browser tab only — sessionStorage, so it's gone when the tab closes and never shared
 * across tabs or people. For drafts someone would be upset to lose to a stray click.
 *
 * Values must be JSON-serializable. Storage can be unavailable or full (blocked site
 * data, some private modes, quota); every access is guarded and the state then simply
 * behaves like plain `useState`. */
export function useSessionState<T>(key: string, initial: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(key);
      return stored === null ? initial : (JSON.parse(stored) as T);
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // unavailable or over quota — keep working in memory
    }
  }, [key, value]);

  return [value, setValue];
}
